import React from 'react';
import {
  ListBase, ListView, TextField, FunctionField, TextInput, useTranslate,
} from 'react-admin';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import {
  ListScreen, PageTitle, RenameRowActions, ResponsiveDatagrid, SectionHeader, useListExport,
} from '@/components/shell';

interface NationalityRecord {
  id: number;
  name: string;
  mappedDocumentTypes?: string[];
}

/** The document types configured for this nationality, or a note that there are none. */
const MappedDocuments: React.FC<{ record: NationalityRecord }> = ({ record }) => {
  const t = useTranslate();
  const docs = record?.mappedDocumentTypes ?? [];
  return (
    <Typography variant="body2" color="text.secondary" component="span">
      {docs.length ? docs.join(', ') : t('resources.nationalities.fields.notConfigured')}
    </Typography>
  );
};

const NationalityActions: React.FC<{ record: NationalityRecord }> = ({ record }) => {
  const t = useTranslate();
  return (
    <RenameRowActions
      name={record?.name || ''}
      nameLabel={t('resources.nationalities.fields.name')}
      onRename={(name) => httpClient.put('/admin/nationality/UpdateNationality', { id: record.id, name })}
      onDelete={() => httpClient.delete(`/admin/nationality/DeleteNationality/${record.id}`)}
    />
  );
};

/** Rendered inside the list's context so Export reads the current search and sort. */
const Header = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const exportAction = useListExport();
  return (
    <SectionHeader
      left={
        <PageTitle
          title={t('resources.nationalities.name', { smart_count: 2 })}
          subtitle={t('screens.nationalities.subtitle')}
        />
      }
      export={{ label: t('ra.action.export'), ...exportAction }}
      create={{ label: t('common.create'), onClick: () => navigate('/nationalities/create') }}
    />
  );
};

export const NationalityList = () => {
  const t = useTranslate();
  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
  ];

  return (
    <ListBase perPage={25} sort={{ field: 'name', order: 'ASC' }}>
      <ListScreen header={<Header />}>
        <ListView filters={filters} actions={false} empty={false}>
          <ResponsiveDatagrid<NationalityRecord>
            rowClick={false}
            mobile={{
              primary: (r) => r.name,
              lines: [{ label: t('screens.nationalities.documents'), render: (r) => <MappedDocuments record={r} /> }],
              actions: (r) => <NationalityActions record={r} />,
            }}
          >
            <TextField source="name" label={t('resources.nationalities.fields.name')} sortBy="name" />
            <FunctionField
              label={t('screens.nationalities.documents')}
              sortable={false}
              render={(r: NationalityRecord) => <MappedDocuments record={r} />}
            />
            <FunctionField
              label=""
              textAlign="right"
              sortable={false}
              render={(r: NationalityRecord) => <NationalityActions record={r} />}
            />
          </ResponsiveDatagrid>
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
